<template>
  <section
    class="relative overflow-hidden border-y border-[#E8E8E8] py-16"
    aria-labelledby="faq-frenos-title"
  >
    <!-- Fondo -->
    <div
      v-if="backgroundImage"
      class="absolute inset-0 bg-cover bg-center bg-no-repeat"
      :style="{ backgroundImage: `url(${backgroundImage})`, backgroundAttachment: 'fixed' }"
      aria-hidden="true"
    >
      <div class="absolute inset-0 bg-white/45" />
    </div>

    <div class="relative mx-auto max-w-[900px] px-5">
      <!-- Encabezado -->
      <div class="mb-7 text-center">
        <div class="mb-2.5 inline-flex items-center gap-2 text-[#C8102E]" aria-hidden="true">
          <span class="h-[2px] w-7 rounded-full bg-[#C8102E]" />

          <span class="text-[13px] font-semibold uppercase leading-none tracking-[0.14em]">
            {{ eyebrow }}
          </span>

          <span class="h-[2px] w-7 rounded-full bg-[#C8102E]" />
        </div>

        <h2
          id="faq-frenos-title"
          class="m-0 text-[28.8px] font-semibold leading-[1.25] text-[#33393F]"
        >
          {{ title }}
        </h2>
      </div>

      <!-- Preguntas -->
      <div class="flex flex-col gap-3">
        <article
          v-for="(item, index) in questions"
          :key="item.id"
          class="overflow-hidden rounded-[12px] border border-[#E8E8E8] bg-[#FCFCFC] transition-shadow duration-200"
          :class="{ 'shadow-[0_8px_24px_rgba(0,0,0,0.07)]': isOpen(index) }"
        >
          <button
            type="button"
            class="flex w-full items-center justify-between gap-4 bg-transparent px-[22px] py-[18px] text-left text-[16.8px] font-semibold leading-[1.3] text-[#33393F] transition-colors duration-200 hover:text-[#C8102E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C8102E] focus-visible:ring-offset-2"
            :aria-expanded="isOpen(index)"
            :aria-controls="`faq-panel-${index}`"
            @click="toggle(index)"
          >
            <span>
              {{ item.question }}
            </span>

            <svg
              class="h-5 w-5 shrink-0 fill-none stroke-[#C8102E] stroke-[2.4] transition-transform duration-200"
              :class="{ 'rotate-180': isOpen(index) }"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M6 9l6 6 6-6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>

          <Transition
            enter-active-class="transition-[grid-template-rows] duration-300 ease-out"
            leave-active-class="transition-[grid-template-rows] duration-300 ease-in"
            enter-from-class="grid-rows-[0fr]"
            enter-to-class="grid-rows-[1fr]"
            leave-from-class="grid-rows-[1fr]"
            leave-to-class="grid-rows-[0fr]"
          >
            <div
              v-show="isOpen(index)"
              :id="`faq-panel-${index}`"
              class="grid overflow-hidden"
            >
              <div class="min-h-0 overflow-hidden px-[22px] pb-[22px] pt-0">
                <p class="m-0 text-[15.5px] leading-[1.7] text-[#55595E]">
                  {{ item.answer }}
                </p>
              </div>
            </div>
          </Transition>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">

export interface FAQItem {
  id: number | string
  question: string
  answer: string
}

interface Props {
  eyebrow?: string
  title?: string
  backgroundImage?: string
  multiple?: boolean
  questions?: FAQItem[]
}

const props = withDefaults(
  defineProps<Props>(),
  {
    eyebrow: 'PREGUNTAS FRECUENTES',

    title: 'Lo que debes saber sobre tus frenos',

    backgroundImage: '/img/frenos/image/banner-bg.webp',

    multiple: false,

    questions: () => [
      {
        id: 1,
        question: '¿Para qué sirven las pastillas de freno?',
        answer:
          'Las pastillas generan la fricción necesaria contra el disco para disminuir la velocidad del vehículo y detenerlo de forma segura.'
      },
      {
        id: 2,
        question: '¿Cuál es la función de los discos de freno?',
        answer:
          'Los discos trabajan junto con las pastillas transformando la energía cinética en calor durante el proceso de frenado.'
      },
      {
        id: 3,
        question: '¿Para qué sirve el cáliper?',
        answer:
          'El cáliper aloja los pistones que presionan las pastillas contra el disco cuando accionas el pedal.'
      },
      {
        id: 4,
        question: 'Siento un chirrido o ruido al frenar',
        answer:
          'Puede indicar desgaste de pastillas, cristalización del material o suciedad. Se recomienda una revisión preventiva.'
      },
      {
        id: 5,
        question: 'Percibo una vibración en el pedal de freno',
        answer:
          'Normalmente está asociada a discos deformados, desgaste irregular o componentes de suspensión que requieren inspección.'
      },
      {
        id: 6,
        question: '¿Cuánto duran las pastillas de freno?',
        answer:
          'Depende del tipo de conducción, vehículo y calidad del componente. Habitualmente pueden durar entre 20.000 y 50.000 km.'
      }
    ]
  }
)

const opened = ref<number[]>([])

function isOpen(index: number): boolean {
  return opened.value.includes(index)
}

function toggle(index: number): void {
  if (props.multiple) {
    opened.value = isOpen(index)
      ? opened.value.filter((item) => item !== index)
      : [...opened.value, index]

    return
  }

  opened.value = isOpen(index) ? [] : [index]
}
</script>